<template>
    <div>
        <ul class="cic-container">
            <li class="cic-header">
                <span class="cic-header-title">{{ $t('Search Media') }}</span>
            </li>
            <li class="media-search">
                <CicTextbox :value="searchText" :placeholder="$t('Search')" clearable @input="filter" />
            </li>
        </ul>
        <Panel
            :title="orgLists ? $t('Organisation Media Lists') : $t('Media Lists')"
            :add-button="$store.hasRight('bAddMedia') ? $t('New Media List') : ''"
            :delete-button="selected.length > 0"
            :page="page"
            :total-rows="filtered.length"
            @delete="handleDelete"
            @input="openForm(-1)"
            @change="setPage"
        >
            <table :id="orgLists ? 'orgmedialists_table' : 'medialists_table'" class="cic-table">
                <thead>
                    <tr>
                        <th>{{ $t('Media List Name') }}</th>
                        <th v-if="$store.hasRight('bDeleteMedia')" class="td-checkbox"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="list in rows" @click="(evt) => handleOpen(list.mediaListId, evt)">
                        <td class="primary">
                            {{ list.name }}
                        </td>
                        <td v-if="$store.hasRight('bDeleteMedia')">
                            <CicCheckbox
                                :value="selected.includes(list.mediaListId)"
                                @click="handleSelected(list.mediaListId)"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </Panel>

        <template v-if="item !== null && !$store.showLoadingOverlay">
            <UploadResults
                v-if="results"
                :error-code="results.errorCode"
                :media="results.items"
                @back="results = null"
                @blur="closePopup"
            />
            <AddMediaList
                v-if="!item.mediaListId"
                :value="item.name"
                @blur="closePopup"
                @input="(val) => (item.name = val)"
                @save="saveItem"
            />
            <EditMediaList
                v-else
                v-show="!results"
                :disable-streaming="disableStreaming"
                :extensions="extensions"
                :item="item"
                :lists="items"
                :max-file-size="maxFileSize"
                :max-queue-length="maxQueueLength"
                :org-list="orgLists"
                :voice-models="voiceModels"
                @blur="closePopup"
                @copy="copy"
                @distribute="distribute"
                @reload="openForm"
            />
        </template>

        <div v-if="$store.showLoadingOverlay" class="cic-overlay"></div>
    </div>
</template>

<script>
    import Panel from '@/components/container/Panel';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import TableUtils from '@/mixins/TableUtils';
    import { deleteItems, load, save } from '@/utils/Http';
    import AddMediaList from './AddMediaList';
    import EditMediaList from './EditMediaList';
    import loadMedia from './MediaUtils';
    import UploadResults from './UploadResults';
    import './resources/styles/medialists.scss';
    import { showValidationMessage } from '@/utils/Notification';

    export default {
        name: 'MediaLists',
        components: {
            Panel,
            UploadResults,
            AddMediaList,
            CicCheckbox,
            CicTextbox,
            EditMediaList,
        },
        mixins: [TableUtils],
        props: {
            orgLists: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                disableStreaming: false,
                extensions: [],
                item: null,
                items: [],
                maxFileSize: 0,
                maxQueueLength: 0,
                results: null,
                searchText: '',
                selected: [],
                page: 1,
                voiceModels: [],
            };
        },
        computed: {
            filtered() {
                if (this.searchText.trim() !== '') {
                    return this.items.filter((x) => x.name.toLowerCase().includes(this.searchText.toLowerCase()));
                }

                return this.items;
            },
            rows() {
                return this.filtered.slice((this.page - 1) * 20, this.page * 20);
            },
        },
        created() {
            this.loadTable();
        },
        methods: {
            closePopup() {
                this.item = null;
                this.results = null;
            },

            copy(listId, mediaIds) {
                if (confirm(this.$t('Are you sure you wish to copy the selected media?'))) {
                    loadMedia(
                        'media/copy',
                        {
                            sourceId: listId,
                            destId: this.item.mediaListId,
                            mediaIds: JSON.stringify(mediaIds),
                        },
                        'right',
                        () => {
                            this.openForm(this.item.mediaListId, true, true);
                        },
                    );
                }
            },

            distribute(prompts, overwrite) {
                const params = {
                    mediaListId: this.item.mediaListId,
                    overwrite: overwrite ? 1 : 0,
                    prompts: JSON.stringify(prompts),
                };
                if (this.orgLists) {
                    params.type = 'organisation';
                }

                loadMedia('media/distribute', params, 'all', (response) => {
                    this.results = response.medialist;
                    this.openForm(this.item.mediaListId, true, true);
                });
            },

            filter(val) {
                this.searchText = val;
                this.page = 1;
            },

            handleDelete() {
                const params = { mediaListIds: this.selected };
                if (this.orgLists) {
                    params.type = 'organisation';
                }

                deleteItems('medialists/delete', params, this.loadTable);
            },

            loadTable() {
                this.page = 1;
                this.selected = [];
                this.closePopup();
                load(this.orgLists ? 'orgmedialists/get' : 'medialists/get', {}, (response) => {
                    this.items = response.lists.map((x) => ({
                        mediaListId: x.mediaListId,
                        name: x.name,
                        media: this.orgLists ? x.prompts : undefined,
                    }));
                    this.disableStreaming = response.disableStreaming;
                    this.extensions = response.extensions;
                    this.maxFileSize = response.maxFileSize;
                    this.maxQueueLength = response.maxQueueLength;
                    this.voiceModels = response.voiceModels;
                });
            },

            openForm(mediaListId, changeItem = true, force = false) {
                if (mediaListId > 0) {
                    const list = this.items.find((x) => x.mediaListId === mediaListId);
                    if (list.media === undefined || force) {
                        if (this.orgLists) {
                            loadMedia('orgmedialists/get', {}, changeItem ? 'right' : 'left', (response) => {
                                this.items = response.lists.map((x) => ({
                                    mediaListId: x.mediaListId,
                                    name: x.name,
                                    media: x.prompts,
                                }));
                                list.media = this.items.find((x) => x.mediaListId === mediaListId).prompts;
                            });
                        } else {
                            loadMedia('media/get', { mediaListId }, changeItem ? 'right' : 'left', (response) => {
                                list.media = response.media;
                                if (changeItem) {
                                    this.item = { ...list };
                                }
                            });
                        }
                    }

                    if (changeItem) {
                        this.item = { ...list };
                    }
                } else {
                    this.item = {
                        mediaListId: null,
                        name: '',
                        media: [],
                    };
                }
            },

            saveItem() {
                if (!this.item.name || this.item.name.trim() === '') {
                    showValidationMessage('Name is a required field');
                    return;
                }

                const params = { name: this.item.name };
                if (this.orgLists) {
                    params.type = 'organisation';
                }

                save('medialist/update', params, this.loadTable);
            },
        },
    };
</script>
